import React, { useState } from "react";
import { Input } from "../ui/input";
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { EyeClosed, EyeIcon } from "lucide-react";
import { Textarea } from "../ui/textarea";

type Props = {
  names: string[]; // Array of field names
  label?: string; // Main label for the group
  batchedLabels?: string[]; // Optional sub-labels for each input (can be empty)
  count: number; // Number of inputs to render
  type?: string;
  required?: boolean;
  disabled?: boolean;
  maxValue?: number[];
  placeholders?: string[]; // Array of placeholders
  defaultValues?: string[]; // Array of default values
  isNumber?: boolean;
  maxLength?: number | undefined;
  isAlphabet?: boolean;
  rightComponents?:any;
};

const RhfBatchedInput = ({
  names,
  label,
  batchedLabels = [],
  count,
  type = "text",
  required = false,
  disabled = false,
  defaultValues = [],
  isNumber = false,
  maxLength,
  maxValue = [],
  isAlphabet = false,
  placeholders = [],
  rightComponents= [],
  ...props
}: Props) => {
  const [showPassword, setShowPassword] = useState<{ [key: number]: boolean }>(
    {}
  );
  const { control } = useFormContext();

  const togglePasswordVisibility = (index: number) => {
    setShowPassword((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className="space-y-2">
      {/* Main Label */}
      {label && (
        <FormLabel>
          {label}
          {required ? "*" : ""}
        </FormLabel>
      )}

      {/* Batched Inputs */}
      <div className="flex gap-2">
        {Array.from({ length: count }).map((_, index) => {
          const fieldName = names[index] || `field_${index}`;
          const fieldLabel = batchedLabels?.[index];
          const fieldPlaceholder = placeholders?.[index];
          const fieldDefaultValue = defaultValues?.[index] || "";

          // console.log(maxValue);
          const fieldMaxValue = maxValue?.[index] || 0;

          return (
            <FormField
              key={fieldName}
              control={control}
              defaultValue={fieldDefaultValue}
              name={fieldName}
              render={({ field }) => (
                <FormItem className="relative flex-1">
                  {/* Optional Sub-label for each input */}
                  {fieldLabel && (
                    <FormLabel className="text-sm text-muted-foreground">
                      {fieldLabel}
                    </FormLabel>
                  )}
                  <FormControl>
                    <div className="relative">
                      {type === "textarea" ? (
                        <Textarea
                          {...field}
                          disabled={!!disabled}
                          placeholder={fieldPlaceholder}
                          {...props}
                        />
                      ) : (
                        <>
                        <Input
                          {...field}
                          onChange={(e) => {
                            let val = e.target.value;
                            let lastChar = val[val.length - 1] || "";
                            let secondLastChar = val[val.length - 2] || "";

                            // Prevent double space
                            if (
                              type === "password"
                                ? lastChar === " "
                                : lastChar === " " && secondLastChar === " "
                            )
                              return;

                            // Only Numbers
                            if (isNumber) {
                              console.log(fieldMaxValue, val);
                              if (val === "" || /^[0-9]+$/.test(val)) {
                                if(fieldMaxValue && Number(val) > Number(fieldMaxValue)) return;
                                field.onChange(
                                  maxLength ? val.substring(0, maxLength) : val
                                );
                              }
                              return;
                            }

                            // Only Alphabets
                            if (isAlphabet) {
                              const alphabetRegex = /^[A-Za-z ]*$/;
                              if (!alphabetRegex.test(val)) return;
                              field.onChange(
                                maxLength ? val.substring(0, maxLength) : val
                              );
                              return;
                            }

                            // Default Input
                            field.onChange(
                              maxLength ? val.substring(0, maxLength) : val
                            );
                          }}
                          type={
                            showPassword[index] && type === "password"
                              ? "text"
                              : type
                          }
                          disabled={!!disabled}
                          placeholder={fieldPlaceholder}
                          {...props}
                        />
                        {rightComponents?.[index] ? rightComponents[index] : null}
                        </>

                      )}
                      {type === "password" && (
                        <div
                          onClick={() => togglePasswordVisibility(index)}
                          className="absolute bottom-2 right-2 cursor-pointer"
                        >
                          {!showPassword[index] ? (
                            <EyeClosed size={"20"} />
                          ) : (
                            <EyeIcon size={"20"} />
                          )}
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        })}
      </div>
    </div>
  );
};

export default RhfBatchedInput;